import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Building2,
  ChevronLeft,
  ChevronRight,
  ChevronRight as Chevron,
  Clock,
  Command,
  Link2,
  Loader2,
  Lock,
  MapPin,
  Pin,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  Star,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { cn } from '@/lib/utils';
import { adminFetch } from '@/hooks/admin/useAdminApi';
import { useAccountPalette } from '@/pages/admin/AdminLayout';
import { useAdminAccounts, type AdminAccountRef } from '@/hooks/admin/useAdminAccounts';
import {
  useAdminCustomers,
  useEnableCustomer,
  useSetCustomerActive,
  type AdminCustomerRow,
  type CustomerLifecycle,
} from '@/hooks/admin/useAdminCustomers';

const PAGE_SIZE = 50;

const LIFECYCLE_TABS: { id: CustomerLifecycle; label: string; hint: string }[] = [
  { id: 'active', label: 'Active', hint: 'Set up on the web' },
  { id: 'inactive', label: 'Inactive', hint: 'Open account, not set up' },
  { id: 'closed', label: 'Closed', hint: 'Deactivated' },
];

export default function AdminCustomers() {
  const [tab, setTab] = useState<CustomerLifecycle>('active');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [offset, setOffset] = useState(0);

  // Debounce search to avoid hammering the function on every keystroke.
  useMemo(() => {
    const t = setTimeout(() => {
      setDebouncedQ(q.trim());
      setOffset(0);
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const { data, isLoading, isFetching, error, refetch } = useAdminCustomers(
    debouncedQ,
    offset,
    PAGE_SIZE,
    tab,
  );
  const enableMutation = useEnableCustomer();
  const setActiveMutation = useSetCustomerActive();

  const { openPalette } = useAccountPalette();
  const { recents, pinned, togglePin, isPinned } = useAdminAccounts();

  const navigate = useNavigate();
  const rows = data?.rows ?? [];
  const hasNext = rows.length >= PAGE_SIZE;
  const errorMessage = error instanceof Error ? error.message : null;

  const detailHrefFor = (row: AdminCustomerRow) =>
    row.orders_customer_id ? `/admin/customers/${row.orders_customer_id}` : null;

  const columns: Column<AdminCustomerRow>[] = [
    {
      key: 'name',
      header: 'Customer',
      cell: (row) => {
        const canPin = row.enabled && !!row.orders_customer_id;
        const pinned = row.orders_customer_id ? isPinned(row.orders_customer_id) : false;
        return (
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 min-w-0">
              {canPin && (
                <button
                  type="button"
                  aria-label={pinned ? `Unpin ${row.name}` : `Pin ${row.name}`}
                  aria-pressed={pinned}
                  title={pinned ? 'Unpin from quick-access' : 'Pin for quick-access'}
                  onClick={(e) => {
                    e.stopPropagation();
                    togglePin({
                      id: row.orders_customer_id!,
                      name: row.name,
                      number: String(row.qbo_customer_id),
                    });
                  }}
                  className={cn(
                    'shrink-0 rounded-md p-0.5 transition-colors cursor-pointer',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brix-primary/40',
                    pinned ? 'text-brix-primary' : 'text-brix-muted hover:text-brix-primary',
                  )}
                >
                  <Star size={13} className={pinned ? 'fill-current' : ''} />
                </button>
              )}
              <div className="font-medium text-brix-ink truncate">{row.name}</div>
            </div>
            {row.enabled && (
              <div className="text-[11px] text-brix-muted mt-0.5 flex items-center gap-1">
                <MapPin size={11} /> {row.location_count} location{row.location_count === 1 ? '' : 's'}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'qbo',
      header: 'QBO ID',
      numeric: true,
      width: '120px',
      cell: (row) => <span className="text-xs text-brix-muted">{row.qbo_customer_id}</span>,
      hideOnMobile: true,
    },
    {
      key: 'status',
      header: 'Status',
      width: '150px',
      cell: (row) => (
        <div className="flex flex-col items-start gap-1">
          <CustomerStatusBadge row={row} />
          {row.enabled && row.on_new_system === true && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700"
              title="Switched over to the new system"
            >
              ✓ New system
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      align: 'right',
      width: '170px',
      cell: (row) => {
        const detailHref = detailHrefFor(row);
        const enabling =
          enableMutation.isPending && enableMutation.variables === row.qbo_customer_id;
        const toggling =
          setActiveMutation.isPending &&
          setActiveMutation.variables?.customer_id === row.orders_customer_id;
        if (!row.enabled) {
          return (
            <div className="flex justify-end">
              <Button
                variant="primary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  enableMutation.mutate(row.qbo_customer_id);
                }}
                disabled={enabling}
              >
                {enabling ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                {enabling ? 'Enabling…' : 'Enable'}
              </Button>
            </div>
          );
        }
        return (
          <div className="flex justify-end gap-1.5 flex-wrap">
            {!row.orders_active && (
              <Button
                variant="secondary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  if (row.orders_customer_id)
                    setActiveMutation.mutate({ customer_id: row.orders_customer_id, active: true });
                }}
                disabled={toggling}
              >
                {toggling ? <Loader2 size={12} className="animate-spin" /> : null}
                Reactivate
              </Button>
            )}
            {detailHref && (
              <Link
                to={detailHref}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-sm text-brix-primary hover:underline cursor-pointer"
              >
                Manage <Chevron size={14} />
              </Link>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="px-6 py-6 space-y-5">
      {/* Pinned + Recents — jump straight to frequent accounts, no typing */}
      <PinnedRecentsStrip pinned={pinned} recents={recents} onTogglePin={togglePin} />

      {/* Lifecycle tabs: Active (on web) / Inactive (never set up) / Closed */}
      <div className="flex items-center gap-2 flex-wrap">
        {LIFECYCLE_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => {
              setTab(t.id);
              setOffset(0);
            }}
            title={t.hint}
            className={cn(
              'px-4 py-1.5 text-sm rounded-full cursor-pointer transition-all duration-200',
              tab === t.id
                ? 'bg-brix-primary-tint text-brix-primary font-semibold shadow-card'
                : 'text-brix-muted hover:bg-brix-primary-tint/60 hover:text-brix-ink',
            )}
          >
            {t.label}
            <span className={cn('ml-1.5 text-[10px]', tab === t.id ? 'opacity-70' : 'opacity-50')}>
              {t.hint}
            </span>
          </button>
        ))}
      </div>

      {/* Search row — per-tab search + ⌘K enabled-account finder */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[14rem] max-w-md">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-brix-muted pointer-events-none"
          />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={
              tab === 'inactive'
                ? 'Search QuickBooks customers to set up…'
                : tab === 'closed'
                  ? 'Search closed accounts…'
                  : 'Search active web customers…'
            }
            className="pl-9"
          />
        </div>
        <Button
          variant="secondary"
          size="md"
          onClick={() => openPalette()}
          title="Find an enabled account (⌘K)"
        >
          <Command size={14} className="text-brix-primary" />
          Find enabled account
          <kbd className="ml-1 inline-flex items-center rounded border border-brix-border bg-brix-surface-alt px-1.5 py-0.5 text-[10px] font-mono text-brix-muted">
            ⌘K
          </kbd>
        </Button>
        <QboSyncButton onDone={() => void refetch()} />
        {tab === 'active' && <RelinkSfButton onDone={() => void refetch()} />}
        {isFetching && (
          <div className="flex items-center gap-1.5 text-xs text-brix-muted">
            <Loader2 size={12} className="animate-spin" />
            Loading
          </div>
        )}
      </div>

      {errorMessage && (
        <div className="rounded-lg border border-brix-warning/30 bg-brix-warning/5 px-3 py-2 text-sm text-brix-warning">
          {errorMessage}{' '}
          <button
            type="button"
            className="ml-2 underline cursor-pointer"
            onClick={() => void refetch()}
          >
            Retry
          </button>
        </div>
      )}

      {/* Table */}
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => String(row.qbo_customer_id)}
        isLoading={isLoading}
        caption="Customers"
        onRowClick={(row) => {
          const href = detailHrefFor(row);
          if (href) navigate(href);
        }}
        empty={
          debouncedQ
            ? 'No customers match this search.'
            : tab === 'inactive'
              ? 'Every open QuickBooks account is already set up on the web.'
              : tab === 'closed'
                ? 'No closed accounts.'
                : 'No active web customers yet — set them up from the Inactive tab.'
        }
      />

      {/* Pagination */}
      <div className="flex items-center justify-between text-sm">
        <div className="text-brix-muted">
          {rows.length > 0
            ? `Showing ${offset + 1}–${offset + rows.length}`
            : 'No results'}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={offset === 0}
            onClick={() => setOffset((v) => Math.max(0, v - PAGE_SIZE))}
          >
            <ChevronLeft size={14} /> Prev
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={!hasNext}
            onClick={() => setOffset((v) => v + PAGE_SIZE)}
          >
            Next <ChevronRight size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
}

/** The customer lifecycle status pill, on the shared StatusBadge. */
function CustomerStatusBadge({ row }: { row: AdminCustomerRow }) {
  if (!row.enabled) return <StatusBadge tone="neutral" size="sm">Not enabled</StatusBadge>;
  if (row.on_credit_hold === true)
    return (
      <StatusBadge tone="danger" size="sm" icon={<Lock size={11} />}>
        Ordering locked
      </StatusBadge>
    );
  if (row.orders_active)
    return (
      <StatusBadge tone="success" size="sm" icon={<ShieldCheck size={11} />}>
        Active
      </StatusBadge>
    );
  return <StatusBadge tone="warning" size="sm">Deactivated</StatusBadge>;
}

/* -------------------- Pinned + Recents quick-access -------------------- */

function PinnedRecentsStrip({
  pinned,
  recents,
  onTogglePin,
}: {
  pinned: AdminAccountRef[];
  recents: AdminAccountRef[];
  onTogglePin: (acct: AdminAccountRef) => void;
}) {
  // Recents minus anything already pinned (pinned takes precedence visually).
  const pinnedIds = new Set(pinned.map((p) => p.id));
  const recentsToShow = recents.filter((r) => !pinnedIds.has(r.id)).slice(0, 6);

  if (pinned.length === 0 && recentsToShow.length === 0) return null;

  return (
    <div className="space-y-3">
      {pinned.length > 0 && (
        <QuickAccessGroup
          title="Pinned"
          icon={<Pin size={11} />}
          accounts={pinned}
          pinned
          onTogglePin={onTogglePin}
        />
      )}
      {recentsToShow.length > 0 && (
        <QuickAccessGroup
          title="Recent"
          icon={<Clock size={11} />}
          accounts={recentsToShow}
          pinned={false}
          onTogglePin={onTogglePin}
        />
      )}
    </div>
  );
}

function QuickAccessGroup({
  title,
  icon,
  accounts,
  pinned,
  onTogglePin,
}: {
  title: string;
  icon: ReactNode;
  accounts: AdminAccountRef[];
  pinned: boolean;
  onTogglePin: (acct: AdminAccountRef) => void;
}) {
  const navigate = useNavigate();
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1.5 text-[10px] font-semibold tracking-tight text-brix-muted uppercase">
        <span className="text-brix-primary">{icon}</span>
        {title}
      </div>
      <div className="flex flex-wrap gap-2">
        {accounts.map((acct) => (
          <div
            key={acct.id}
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/admin/customers/${acct.id}`)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate(`/admin/customers/${acct.id}`);
              }
            }}
            className={cn(
              'group flex items-center gap-2 rounded-lg border border-brix-border bg-brix-surface',
              'px-3 py-2 cursor-pointer transition-all duration-200',
              'hover:border-brix-primary/40 hover:bg-brix-primary-tint/40',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brix-primary/40',
            )}
            title={`Manage ${acct.name}`}
          >
            <Building2 size={14} className="text-brix-primary shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-medium text-brix-ink truncate max-w-[14rem]">
                {acct.name}
              </div>
              {acct.number && (
                <div className="text-[10px] text-brix-muted">Acct #{acct.number}</div>
              )}
            </div>
            <button
              type="button"
              aria-label={pinned ? `Unpin ${acct.name}` : `Pin ${acct.name}`}
              aria-pressed={pinned}
              title={pinned ? 'Unpin' : 'Pin'}
              onClick={(e) => {
                e.stopPropagation();
                onTogglePin(acct);
              }}
              className={cn(
                'shrink-0 rounded-md p-1 transition-colors cursor-pointer',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brix-primary/40',
                pinned
                  ? 'text-brix-primary'
                  : 'text-brix-muted opacity-0 group-hover:opacity-100 hover:text-brix-primary',
              )}
            >
              <Star size={13} className={pinned ? 'fill-current' : ''} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Re-link Service Fusion — batch-heal customers with no SF link      */
/* ------------------------------------------------------------------ */

interface RelinkResult {
  processed: number;
  linked: Array<{ id: string; name: string; sf_customer_id: number; matched_by: string }>;
  unmatched: Array<{ id: string; name: string }>;
  remaining: number;
}

/**
 * Runs every active customer with no sf_customer_id through the server-side
 * SF resolver (admin-relink-sf-customers) and reports the outcome. The
 * endpoint is time-budgeted and resumable, so this loops until remaining is
 * 0. Customers the resolver can't match need the manual link-search on their
 * Locations tab — the summary links straight there.
 */
/**
 * Sync from QuickBooks — same endpoint as the Payments page's button
 * (admin-sync-invoices runs the invoice CDC sync + the customer-list
 * sync), surfaced here so merges/renames/new customers refresh without
 * leaving the Customers tab.
 */
function QboSyncButton({ onDone }: { onDone: () => void }) {
  const [state, setState] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [nameDiffCount, setNameDiffCount] = useState(0);

  async function run() {
    setState('running');
    try {
      const res = await adminFetch<{ name_differences?: number }>('admin-sync-invoices', {
        method: 'POST',
      });
      setNameDiffCount(res.name_differences ?? 0);
      setState('done');
      onDone();
      setTimeout(() => setState('idle'), 6000);
    } catch {
      setState('error');
      setTimeout(() => setState('idle'), 6000);
    }
  }

  return (
    <Button
      variant="secondary"
      size="sm"
      disabled={state === 'running'}
      onClick={() => void run()}
      title="Pull the latest customer list (names, merges, new customers) and changed invoices from QuickBooks now"
    >
      {state === 'running' ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
      {state === 'running'
        ? 'Syncing…'
        : state === 'done'
          ? nameDiffCount > 0
            ? `Synced ✓ — ${nameDiffCount} name${nameDiffCount === 1 ? '' : 's'} differ from QuickBooks (not changed here)`
            : 'Synced ✓'
          : state === 'error'
            ? 'Sync failed — retry'
            : 'Sync from QuickBooks'}
    </Button>
  );
}

function RelinkSfButton({ onDone }: { onDone: () => void }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<RelinkResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const totals: RelinkResult = { processed: 0, linked: [], unmatched: [], remaining: 0 };
      // The endpoint stops after ~18s of SF work and reports `remaining` —
      // keep calling until the sweep is done (bounded: it always advances).
      for (let round = 0; round < 20; round += 1) {
        const res = await adminFetch<RelinkResult & { ok: true }>('admin-relink-sf-customers', {
          method: 'POST',
          body: JSON.stringify({}),
        });
        totals.processed += res.processed;
        totals.linked.push(...res.linked);
        totals.unmatched.push(...res.unmatched);
        totals.remaining = res.remaining;
        if (res.remaining <= 0 || res.processed === 0) break;
      }
      setResult(totals);
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Re-link sweep failed');
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <Button
        variant="secondary"
        size="md"
        disabled={running}
        onClick={() => void run()}
        title="Auto-match every active customer that isn't linked to a Service Fusion record — unlinked customers can't submit orders"
      >
        {running ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} className="text-brix-primary" />}
        {running ? 'Re-linking…' : 'Re-link Service Fusion'}
      </Button>

      {(result || error) && (
        <div className="basis-full rounded-lg border border-brix-border bg-brix-surface-alt/40 px-3.5 py-2.5 text-xs space-y-1.5">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1.5 min-w-0">
              {error && <div className="text-brix-warning">{error}</div>}
              {result && (
                <>
                  <div className="text-brix-ink">
                    {result.processed === 0 ? (
                      <>Every active customer is already linked to Service Fusion — nothing to do.</>
                    ) : (
                      <>
                        <span className="font-semibold">{result.linked.length} linked automatically</span>
                        {result.unmatched.length > 0 && (
                          <>
                            {' · '}
                            <span className="font-semibold text-amber-700">
                              {result.unmatched.length} need a manual link
                            </span>
                          </>
                        )}
                      </>
                    )}
                  </div>
                  {result.linked.length > 0 && (
                    <div className="text-brix-muted">
                      Linked: {result.linked.map((l) => `${l.name} → SF #${l.sf_customer_id}`).join(' · ')}
                    </div>
                  )}
                  {result.unmatched.length > 0 && (
                    <div className="text-brix-muted">
                      No SF record auto-matches these — open each and link by hand:{' '}
                      {result.unmatched.map((u, i) => (
                        <span key={u.id}>
                          {i > 0 && ' · '}
                          <Link
                            to={`/admin/customers/${u.id}/locations`}
                            className="text-brix-primary hover:underline"
                          >
                            {u.name}
                          </Link>
                        </span>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            <button
              type="button"
              aria-label="Dismiss"
              className="shrink-0 text-brix-muted hover:text-brix-ink cursor-pointer"
              onClick={() => {
                setResult(null);
                setError(null);
              }}
            >
              <X size={13} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
